
import type { ModalKeys, ScenarioFormDataType } from "./scenario-types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GeoFeatureSvg, GroundStationSvg, PresimADCSSvg, PresimDataFlowSvg, PresimPowerSvg, SunExposureSvg, PresimThermalSvg, ScheduleIconSvg, FaultInjectIconSvg } from '../../../CommonComponents/svgs/Svgs';
import { WindowSize } from "../../../CommonComponents/modals/Modal";
import { TimelineFilterStateType } from "../../../CommonComponents/timeline/TimelineFilter";
dayjs.extend(utc);

export const getInitialFormData = (): ScenarioFormDataType => ({
    details: {
        scenarioName: "",
        scenarioStartTime: dayjs().utc().unix(),
        scenarioDuration: 1440,
        sampleRate: 1,
        scenarioOperation: "CONOPS",
        scenarioDeploymentType: "SIMULATE",
    },
    satellites: [],
    hwil: {
        hwInfo: [],
        remoteAppConfiguration: {
            remoteApp: { AppData: [] },
            remoteBus: {},
        },
    },
    initial_altitude: {
        orientation: { roll: 0, pitch: 0, yaw: 0 },
        angularRate: { omegaX: 0, omegaY: 0, omegaZ: 0 },
    },
    segments: { contactType: "Continuous", stations: [] },
    schedule: {
        scheduleType: "custom",
        taskplans: [],
        importedSchedule: null,
        simulationStartTime: dayjs().utc().unix(),
        simulationDuration: 180,
    },
    faults: [],
    assets: {
        schedule: {
            status: 'idle',
            data: null,
        },
        hwilAssets: {
            status: 'idle',
            data: null,
        },
        faultPlans: {
            status: 'idle',
            data: null,
        },
        presim: {
            fidelityType: "high",
            data: null,
        },
        orbitalInsights: {
            status: 'idle',
            data: null,
        }
    },
});


export const WINDOW_INITIAL_Z_INDEX = 99
export const WINDOW_SIZE = {
    SMALL: 'small' as WindowSize,
    MEDIUM: 'medium' as WindowSize,
    LARGE: 'large' as WindowSize
}
export const WINDOW_HEIGHT = {
    LARGE: 70,
    MEDIUM: 60,
    SMALL: 50
}
export const WINDOW_WIDTH = {
    LARGE: 60,
    MEDIUM: 40,
    SMALL: 20
}
export const SCHEDULE_REPORT_WINDOW_WIDTH = {
    LARGE: 70,
    MEDIUM: 50,
    SMALL: 50
}

export const getReportWindowWidth = (modalKey: ModalKeys, windowSize: WindowSize) => {
    const widths = modalKey === 'scheduleReport' ? SCHEDULE_REPORT_WINDOW_WIDTH : WINDOW_WIDTH

    if (windowSize === WINDOW_SIZE.SMALL) return widths.SMALL
    if (windowSize === WINDOW_SIZE.LARGE) return widths.LARGE
    return widths.MEDIUM
}

export const REPORT_WINDOWS = {
    orbitReport: {
        tabs: [
            {
                label: 'Exposure',
                value: 'exposure',
                icon: <SunExposureSvg />,
            },
            {
                label: 'Ground Contacts',
                value: 'contacts',
                icon: <GroundStationSvg />,
            },
            {
                label: 'GeoFeature',
                value: 'features',
                icon: <GeoFeatureSvg />,
            },
        ],
        open: false,
        pinned: false,
        zIndex: WINDOW_INITIAL_Z_INDEX,
        windowSize: WINDOW_SIZE.MEDIUM,
        position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM },
        selected: 'exposure',
        windows: {
            'exposure': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
            'contacts': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
            'features': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
        }
    },
    scheduleReport: {
        tabs: [{
            label: 'SCHEDULE',
            value: 'schedule',
            icon: <ScheduleIconSvg />,
        }],
        open: false,
        pinned: false,
        zIndex: WINDOW_INITIAL_Z_INDEX,
        windowSize: WINDOW_SIZE.MEDIUM,
        position: { height: WINDOW_HEIGHT.MEDIUM, width: SCHEDULE_REPORT_WINDOW_WIDTH.MEDIUM },
        selected: 'schedule',
        windows: {
            'schedule': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: SCHEDULE_REPORT_WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
        }
    },
    presimReport: {
        tabs: [
            {
                label: 'POWER',
                value: 'power',
                icon: <PresimPowerSvg />,
            },
            {
                label: 'ADCS',
                value: 'adcs',
                icon: <PresimADCSSvg />,
            },
            {
                label: 'DATA FLOW',
                value: 'dataflow',
                icon: <PresimDataFlowSvg />,
            },
            {
                label: 'THERMAL',
                value: 'thermal',
                icon: <PresimThermalSvg />,
            },
        ],
        open: false,
        pinned: false,
        zIndex: WINDOW_INITIAL_Z_INDEX,
        windowSize: WINDOW_SIZE.MEDIUM,
        position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM },
        selected: 'power',
        windows: {
            'power': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
            'adcs': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
            'dataflow': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
            'thermal': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
        }
    },
    faultReport: {
        tabs: [{
            label: 'FAULT INJECTION',
            value: 'faults',
            icon: <FaultInjectIconSvg />,
        }],
        open: false,
        pinned: false,
        zIndex: WINDOW_INITIAL_Z_INDEX,
        windowSize: WINDOW_SIZE.MEDIUM,
        position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM },
        selected: 'faults',
        windows: {
            'faults': { open: false, detached: false, windowSize: WINDOW_SIZE.MEDIUM, position: { height: WINDOW_HEIGHT.MEDIUM, width: WINDOW_WIDTH.MEDIUM }, pinned: false, zIndex: WINDOW_INITIAL_Z_INDEX },
        }
    },
}

export const DATA_SOURCE_NAMES = [
    'GS_OBJECT',
    'FAULT_AOI_OBJECT',
    'FAULT_GT_OBJECT',
    'TASKPLAN_AOI_OBJECT',
    'TASKPLAN_GT_OBJECT',
    'MULTI_GROUND_OBJECT',
    'ORBIT_OBJECT',
    'PREDICTION_ORBIT',
]


export const DEFAULT_TIMELINE_FILTER: TimelineFilterStateType = {
    active: false,
    placement: 'top',
    filter: [
        {
            id: 'tasks',
            name: 'Tasks',
            selected: true,
            filterOptions: [],
            selectedOptions: [],
        },
        {
            id: 'task_schedule',
            name: 'Task Schedule',
            selected: false,
            selectedOptions: [],
            filterOptions: [],
        },
        {
            id: 'task_type',
            name: 'Task Type',
            selected: false,
            selectedOptions: [],
            filterOptions: [],
        }
    ]
}